<template>
  <div class="widget-field-content-editor">
    <div
      v-if="isLoading"
      style="height: 64px"
    >
      <widget-skeleton
        class="mt-4 ml-3"
        type="QChip"
        width="132px"
        height="10px"
      />
    </div>

    <widget-field-base-content-editor
      v-else
      ref="baseContentEditor"
      class="widget-field-content-editor__editor"
      :model-value="modelValue"
      :placeholder="placeholder"
      :max-length="maxLength"
      @update:model-value="emit('update:model-value', $event)"
      @keydown.enter.exact.prevent="submit"
      @paste-files="handleFiles"
    />

    <widget-scroll-with-shadow
      v-if="attachments.length && !isLoading"
      max-height="264px"
    >
      <widget-editor-attachment-list
        class="widget-field-content-editor__attachments"
        :attachments="attachments"
        @remove-attachment="emit('remove-attachment', $event)"
        @retry-attachment="emit('retry-attachment', $event)"
      />
    </widget-scroll-with-shadow>

    <div class="widget-field-content-editor__controls editor-controls">
      <div class="editor-controls__left">
        <widget-emoji-dropdown @select-emoji="onSelectEmoji" />
        <widget-upload
          multiple
          :accept="messengerAcceptedFileExtensions"
          @files-picked="handleFiles"
        >
          <template v-slot="{ browseFile }">
            <widget-button
              is-icon-only
              color="secondary"
              view="transparent"
              size="28"
              @click="browseFile()"
            >
              <widget-icon
                name="tmi-attachment"
                size="20px"
              />
            </widget-button>
          </template>
        </widget-upload>
      </div>

      <div class="editor-controls__right">
        <widget-skeleton
          v-if="isLoading"
          type="circle"
          width="20px"
          height="20px"
        />
        <widget-button
          v-else
          is-icon-only
          view="hover-transparent"
          size="28"
          :is-disabled="!canSend"
          @click="submit"
        >
          <widget-icon
            name="tmi-send"
            size="24px"
            class="submit-icon"
            :class="{
              'submit-icon--disabled': !canSend,
            }"
          />
        </widget-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import WidgetSkeleton from '@widgets/shared/components/WidgetSkeleton.vue'
import WidgetEmojiDropdown from '@widgets/shared/components/dropdown/WidgetEmojiDropdown.vue'
import WidgetFieldBaseContentEditor from '@widgets/shared/components/fields/WidgetFieldBaseContentEditor.vue'
import WidgetUpload from '@widgets/shared/components/WidgetUpload.vue'
import { isWidgetUploadingAttachment, type WidgetAttachment } from '@widgets/shared/types'
import WidgetEditorAttachmentList from '@widgets/shared/components/attachment/editor/WidgetEditorAttachmentList.vue'
import WidgetScrollWithShadow from '@widgets/shared/components/WidgetScrollWithShadow.vue'
import { messengerAcceptedFileExtensions } from '@widgets/messenger/const'
import { computed, onMounted, useTemplateRef, watch } from '@/composition/vue/compositionApi'

import type { Emoji } from '@/services/forms/tmTiptap/types'

const props = defineProps<{
  modelValue: string
  attachments: WidgetAttachment[]
  placeholder?: string
  isLoading?: boolean
  autofocus?: boolean
  autofocusAfterSubmit?: boolean
  maxLength?: number
}>()

const emit = defineEmits<{
  (e: 'update:model-value', value: string): void
  (e: 'submit'): void
  (e: 'files-picked', files: File[]): void
  (e: 'remove-attachment', attachmentId: string): void
  (e: 'retry-attachment', attachmentId: string): void
}>()

const baseContentEditorRef = useTemplateRef<InstanceType<typeof WidgetFieldBaseContentEditor>>('baseContentEditor')
const hasNotUploadedAttachments = computed(() =>
  props.attachments.some((attachment) => isWidgetUploadingAttachment(attachment)),
)
const hasSomethingToSend = computed(() => !!props.modelValue || !!props.attachments.length)
const canSend = computed(() => hasSomethingToSend.value && !props.isLoading && !hasNotUploadedAttachments.value)

const focus = () => {
  baseContentEditorRef.value?.focus()
}

const submit = () => {
  if (!canSend.value) return
  emit('submit')
  if (props.autofocusAfterSubmit) {
    focus()
  }
}

const onSelectEmoji = (emoji: Emoji) => {
  baseContentEditorRef.value?.insertEmoji(emoji)
}

const handleFiles = (files: File[]) => {
  emit('files-picked', files)
}

watch(
  () => props.autofocus,
  (value) => {
    if (value) {
      focus()
    }
  },
)

onMounted(() => {
  if (props.autofocus) {
    focus()
  }
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';

.widget-field-content-editor {
  background: $white;
  border-radius: $border-radius-x;
  border: 1px solid $field-border-color;
  box-shadow: $input-shadow-5;

  &:focus-within {
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.16);
  }

  &__editor {
    height: 64px;
    padding: 11px 14px 0;
  }

  &__attachments {
    padding: 8px 10px 0;
  }
}

.editor-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  height: 40px;
  color: $distinct;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.submit-icon {
  color: $blue;

  &--disabled {
    color: $blue300;
  }
}
</style>
